import React, { useState, useRef, useEffect, useMemo } from 'react';

export interface GlassSelectOption {
  value: string;
  label: string;
}

export interface GlassSelectProps {
  options: GlassSelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Show a type-to-filter input inside the dropdown. Recommended for long lists. */
  searchable?: boolean;
  /** Allow committing the typed query as a free-text value not present in options. */
  allowCustom?: boolean;
  /** Cap how many options render at once (perf for large catalogues). Default 100. */
  maxVisible?: number;
}

export const GlassSelect: React.FC<GlassSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  label,
  disabled = false,
  className = '',
  error,
  size = 'md',
  searchable = false,
  allowCustom = false,
  maxVisible = 100,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear the filter and focus the search box each time the dropdown opens.
  useEffect(() => {
    if (isOpen && searchable) {
      setQuery('');
      // focus after the open transition starts
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [isOpen, searchable]);

  // Filtered + capped option set. When a custom value is allowed and the query
  // matches no existing label, surface a "Use \"…\"" entry so free text can be committed.
  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
    const capped = filtered.slice(0, maxVisible);
    if (allowCustom && query.trim()) {
      const exact = options.some((o) => o.value.toLowerCase() === q);
      if (!exact) {
        return [{ value: query.trim(), label: `Use "${query.trim()}"` }, ...capped];
      }
    }
    return capped;
  }, [options, query, allowCustom, maxVisible]);

  const totalMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)).length : options.length;
  }, [options, query]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setQuery('');
    setIsOpen(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleOptions.length > 0) handleSelect(visibleOptions[0].value);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const containerStyles: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const labelStyles: React.CSSProperties = {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-medium)',
    color: 'var(--text-secondary)',
    marginBottom: 'var(--space-1)',
    display: 'block',
  };

  // Show the raw value when it doesn't match a known option (e.g. a custom font),
  // so an existing free-text value remains visible in the trigger.
  const triggerLabel = selectedOption ? selectedOption.label : (value || placeholder);
  const triggerIsPlaceholder = !selectedOption && !value;

  const getTriggerStyles = (): React.CSSProperties => {
    const sizeStyles = {
      sm: { padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-sm)' },
      md: { padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-base)' },
      lg: { padding: 'var(--space-4) var(--space-5)', fontSize: 'var(--text-lg)' },
    };

    return {
      width: '100%',
      ...sizeStyles[size],
      fontFamily: 'var(--font-family)',
      color: triggerIsPlaceholder ? 'var(--text-muted)' : 'var(--text)',
      background: 'var(--panel)',
      border: `1px solid ${error ? 'var(--danger)' : isOpen ? 'var(--primary-500)' : 'var(--stroke)'}`,
      borderRadius: 'var(--radius-md)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all var(--transition-normal)',
      outline: 'none',
      boxShadow: isOpen ? '0 0 0 3px var(--primary-100)' : 'none',
    };
  };

  const arrowStyles: React.CSSProperties = {
    transition: 'transform var(--transition-normal)',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    color: 'var(--text-muted)',
  };

  const dropdownStyles: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + var(--space-1))',
    left: 0,
    right: 0,
    background: 'var(--panel-strong)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid var(--stroke)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 1000,
    maxHeight: '280px',
    overflowY: 'auto',
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? 'visible' : 'hidden',
    transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
    transition: 'all var(--transition-normal)',
  };

  const searchWrapStyles: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    padding: 'var(--space-2)',
    background: 'var(--panel-strong)',
    borderBottom: '1px solid var(--divider)',
  };

  const searchInputStyles: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-2) var(--space-3)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-family)',
    color: 'var(--text)',
    background: 'var(--panel)',
    border: '1px solid var(--stroke)',
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const optionStyles = (isSelected: boolean): React.CSSProperties => ({
    padding: 'var(--space-3) var(--space-4)',
    fontSize: 'var(--text-base)',
    color: 'var(--text)',
    cursor: 'pointer',
    background: isSelected ? 'var(--primary-100)' : 'transparent',
    transition: 'background var(--transition-fast)',
    borderBottom: '1px solid var(--divider)',
  });

  const metaStyles: React.CSSProperties = {
    padding: 'var(--space-2) var(--space-4)',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-muted)',
  };

  const errorStyles: React.CSSProperties = {
    fontSize: 'var(--text-xs)',
    color: 'var(--danger)',
    marginTop: 'var(--space-1)',
  };

  return (
    <div className={`glass-select-container ${className}`} ref={containerRef}>
      {label && <label style={labelStyles}>{label}</label>}
      <div style={containerStyles}>
        <button
          type="button"
          style={getTriggerStyles()}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {triggerLabel}
          </span>
          <span style={arrowStyles}>▼</span>
        </button>
        <div style={dropdownStyles}>
          {searchable && (
            <div style={searchWrapStyles}>
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder="Type to search…"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                style={searchInputStyles}
              />
            </div>
          )}
          {isOpen && visibleOptions.map((option) => (
            <div
              key={option.value}
              style={optionStyles(option.value === value)}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.currentTarget.style.background = 'var(--panel-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {option.label}
            </div>
          ))}
          {isOpen && visibleOptions.length === 0 && (
            <div style={metaStyles}>No matches.</div>
          )}
          {isOpen && totalMatches > visibleOptions.length && (
            <div style={metaStyles}>
              Showing {visibleOptions.length} of {totalMatches} — keep typing to narrow.
            </div>
          )}
        </div>
      </div>
      {error && <span style={errorStyles}>{error}</span>}
    </div>
  );
};

export default GlassSelect;
